import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseEntityService } from '../entity-services/base-entity.service';
import { INotificationsService } from '../../interfaces/home/notifications-service-interface';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { ConfigService } from '../config.service';
import { ILogger, IShortLogger, Log, Logger } from '../logger.service';
import { ToastQueueService } from '../toast-queue.service';
interface Notification {
  id: string;
  toUser: string,
  timeStamp: string,
  contents: string,
  notifiableIfRead: boolean,
  notificationType: string,
  actionType: string,
  actionData: string,
  isRead: boolean,
  status: string,
  fromUser: string,
  fromUserName?: string,
  fromUserTeam?: string
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService implements INotificationsService {

  private readonly logger: IShortLogger<NotificationsService> = Log.create(NotificationsService);

  private notificationsSubject: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);

  /** Emits the current unread notification count — consumed by bell, sidebar, etc. */
  public readonly unreadCount$: Observable<number> = this.notificationsSubject.pipe(
    map((notifications: Notification[]) => notifications.filter(n => !n.isRead).length)
  );

  private readonly monitoringStartedSubject = new Subject<{ trainerUserId: string; classId: string }>();
  private readonly monitoringStoppedSubject = new Subject<{ classId: string }>();
  private hubConnection?: HubConnection;
  private isDisconnecting = false;

  public baseUrl: string;
  public apiUrl: string;

  constructor(
    public http: HttpClient,
    private configService: ConfigService,
    private toastQueue: ToastQueueService
  ) {
    this.baseUrl = this.configService.getBaseUrl();
    this.apiUrl = this.configService.getApiUrl();
  }

  getNotifactionsForUsers(toUserId: string): Observable<any> {
    this.logger.d(`Fetching notifications for user: ${toUserId}`);
    return this.http.get(`${this.apiUrl}/v1/requisitions/getnotificationsforuser/${toUserId}`);
  }

  markNotificationAsRead(notificationId: string): Observable<any> {
    this.logger.d(`Marking notification as read: ${notificationId}`);
    // Optimistic update — remove from shared subject immediately so badge refreshes
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next(current.filter(n => n.id !== notificationId));
    return this.http.put(`${this.apiUrl}/v1/requisitions/markread/${notificationId}`, {});
  }

  /**
   * Fetches notifications from the server and populates the shared subject.
   * All subscribers to unreadCount$ update automatically.
   */
  loadNotificationsForUser(userId: string): void {
    this.getNotifactionsForUsers(userId).subscribe({
      next: (notifications: Notification[]) => this.notificationsSubject.next(notifications ?? []),
      error: (err) => this.logger.e('Failed to load notifications for user', err)
    });
  }

  private subscribeSignalRMethods() {
    this.logger.d('Subscribing to SignalR methods for notifications');

    this.hubConnection?.on('ReceivedNotification', (notification: Notification) => {
      this.logger.d(`Received notification via SignalR: ${notification.id}`);
      const currentNotifications = this.notificationsSubject.value;

      // Guard: if this notification ID already exists, it's an update (e.g. a read-status
      // echo from the backend) — update in place but do NOT toast again.
      const isNew = !currentNotifications.some(n => n.id === notification.id);

      if (isNew) {
        currentNotifications.push(notification);
      } else {
        const idx = currentNotifications.findIndex(n => n.id === notification.id);
        if (idx !== -1) currentNotifications[idx] = notification;
      }
      this.notificationsSubject.next([...currentNotifications]);

      if (!isNew) return; // don't toast state-update echoes

      // Build display name: "Name (Team)" or fallback to userId
      let fromDisplay = notification.fromUser;
      if (notification.fromUserName) {
        fromDisplay = notification.fromUserTeam
          ? `${notification.fromUserName} (${notification.fromUserTeam})`
          : notification.fromUserName;
      }

      this.toastQueue.push({
        fromDisplay:        fromDisplay ?? '',
        notificationType:   notification.notificationType ?? '',
        message:            notification.contents ?? '',
        timestamp:          new Date()
      });
    });
    this.logger.d('Subscribed to ReceivedNotification method');

    this.hubConnection?.on('MarkedAsRead', (markedAsRead: Notification) => {
      this.logger.d(`Notification marked as read via SignalR: ${markedAsRead.id}`);
      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.filter(n => n.id !== markedAsRead.id);
      this.notificationsSubject.next([...updatedNotifications]);
      // No toast — this is an internal state update
    });
    this.logger.d('Subscribed to MarkedAsRead method');

    this.hubConnection?.on('NotificationSeen', (notification: Notification) => {
      this.logger.d(`Notification seen via SignalR: ${notification.id}`);
      // No toast — informational only
    });
    this.logger.d('Subscribed to NotificationSeen method');

    this.hubConnection?.on("MarkedAllAsRead", () => {
      this.logger.d(`All notifications marked as read via SignalR`);
      this.notificationsSubject.next([]);
      // No toast — internal state update
    });
    this.logger.d('Subscribed to MarkedAllAsRead method');

    this.hubConnection?.on('MonitoringStarted', (payload: { trainerUserId: string; classId: string }) => {
      this.logger.d(`MonitoringStarted received via SignalR: classId=${payload?.classId}`);
      this.monitoringStartedSubject.next(payload);
    });

    this.hubConnection?.on('MonitoringStopped', (payload: { classId: string }) => {
      this.logger.d(`MonitoringStopped received via SignalR: classId=${payload?.classId}`);
      this.monitoringStoppedSubject.next(payload);
    });
  }

  private async startSignalRConnection() {
    this.logger.d('Starting SignalR connection for notifications');

    // Use ConfigService's getBaseUrl() method to get base URL for SignalR hub
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${this.baseUrl}/hubs/NotificationHub`, {
        withCredentials: false
      })
      .build();

    try {
      this.logger.d('Attempting to start SignalR connection...');
      await this.hubConnection.start();
      this.logger.d('SignalR connection started successfully.');
      this.subscribeSignalRMethods();
    } catch (ex) {
      this.logger.e('Error while starting SignalR connection:', ex);
    }
  }

  async joinUserGroup(userId: string) {
    this.logger.d(`Joining user group for notifications: ${userId}`);
    try {
      await this.startSignalRConnection();
      await this.hubConnection?.invoke("JoinUserGroup", userId);
      this.logger.d(`Joined user group: ${userId}`);
    } catch (ex) {
      this.logger.e('Error while joining user group:', ex);
    }
  }

  /**
   * Disconnect from SignalR hub (used during logout)
   * @param userId Optional user ID to leave group before disconnecting
   */
  async disconnect(userId?: string): Promise<void> {
    // Prevent multiple simultaneous disconnect calls
    if (this.isDisconnecting) {
      this.logger.d('Disconnect already in progress, skipping duplicate call');
      return;
    }

    this.logger.d('Disconnecting from SignalR hub');
    this.isDisconnecting = true;

    try {
      if (this.hubConnection && this.hubConnection.state === 'Connected') {
        // Leave user group first if userId provided
        if (userId) {
          try {
            this.logger.d(`Leaving user group before disconnect: ${userId}`);
            await this.hubConnection.invoke("LeaveUserGroup", userId);
            this.logger.d(`Left user group: ${userId}`);
          } catch (leaveError: any) {
            // Log but don't fail - connection might already be closing
            if (leaveError?.message?.includes('canceled') || leaveError?.message?.includes('closed')) {
              this.logger.d('LeaveUserGroup invocation canceled (connection closing)');
            } else {
              this.logger.w('Error leaving user group:', leaveError);
            }
          }
        }

        // Now stop the connection
        await this.hubConnection.stop();
        this.hubConnection = undefined;
        this.logger.i('SignalR connection closed');
      } else if (this.hubConnection) {
        this.logger.d(`Connection not in Connected state (${this.hubConnection.state}), clearing reference`);
        this.hubConnection = undefined;
      }
    } catch (ex: any) {
      // Don't throw for expected connection closure errors
      if (ex?.message?.includes('canceled') || ex?.message?.includes('closed')) {
        this.logger.d('Disconnect completed with expected connection closure');
      } else {
        this.logger.e('Error while disconnecting from SignalR:', ex);
      }
    } finally {
      this.isDisconnecting = false;
    }
  }

  getNotificationStream(): Observable<Notification[]> {
    return this.notificationsSubject.asObservable();
  }

  /** Emits whenever a trainer joins a participant's VM session for monitoring. */
  getMonitoringStartedStream(): Observable<{ trainerUserId: string; classId: string }> {
    return this.monitoringStartedSubject.asObservable();
  }

  /** Emits whenever the trainer leaves the monitored VM session. */
  getMonitoringStoppedStream(): Observable<{ classId: string }> {
    return this.monitoringStoppedSubject.asObservable();
  }
}
